/**
 * OnboardingCoach — the first-orbit coach-mark tour.
 *
 * A step sequence of dismissible bubbles that point at REAL targets: two
 * node steps (a featured hub, centred by the camera), then the KEY panel,
 * the bottom-right controls, the settings gear, and the contribute button.
 * Each non-node step also flashes the live element it points at (via the
 * `coachHighlight` store field). Auto-starts on the first map view (gated
 * on `localStorage['bjjmap:seen-tour']`), or when `tourRunning` flips true
 * from the Settings → "replay tour" affordance.
 *
 * The bubble is re-placed every animation frame against its anchor (so it
 * tracks the drawer sliding open, resizes, and the featured-node framing).
 * Honours prefers-reduced-motion.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppStore } from '../state/store';
import { SEEN_TOUR, flag } from './onboarding';
import { usePrefersReducedMotion } from './useReducedMotion';
import { useViewport } from './useViewport';
import {
  FONT,
  GLASS,
  GOLD,
  GOLD_INK,
  INK,
  INK2,
  INK3,
  LINE2,
  RADIUS,
  hexA,
} from './tokens';

type Anchor =
  | 'key'
  | 'node'
  | 'canvas'
  | 'contribute'
  | 'controls'
  | 'settings'
  | 'search-tab';
type Step = { anchor: Anchor; title: string; body: string; cta: string };

// First Orbit — the four-beat first-run tour. Copy + anchors are verbatim
// from the "First Orbit — Tutorial Spec" treatment: move → read → open →
// grow. The two node steps feature a real node (CoachNodeAnchor in
// Scene.tsx selects it and the camera centres it); the bubble anchors to
// the canvas centre, where that node sits.
// `{desktop/mobile}` tokens in copy are resolved per device at render
// (see deviceText) — e.g. "{Click/Tap} to open".
const TOUR: Step[] = [
  {
    anchor: 'node',
    title: 'Welcome to bjjmap!',
    body: `This is jiu-jitsu, even if it might not look like it! 
    Positions are shown as the colored nodes. Moves are shown as the lines the connect them. 
    Explore the bjj universe by dragging to orbit, {scroll/pinch} to zoom into the details.`,
    cta: 'Show me around →',
  },
  {
    anchor: 'node',
    title: '{Click/Tap} to view details',
    body: "Notes, videos, & step-by-step instructions. {Click/Tap} through a path until it's Game Over.",
    cta: 'Next →',
  },
  {
    anchor: 'search-tab',
    title: 'Search & filter',
    body: 'Find any position or move by name, type, or tag. Stack tags, then toggle ALL / ANY for precision searches.',
    cta: 'Next →',
  },
  {
    anchor: 'controls',
    title: 'Controls live down here',
    body: "Go fullscreen for full immersion, {click/tap} X to deselect the current element, or exit plan mode when you're done building.",
    cta: 'Next →',
  },
  {
    anchor: 'settings',
    title: 'Explore more settings',
    body: "Settings change what's displayed on the map. Try them out to see how deep the game goes.",
    cta: 'Next →',
  },
  {
    anchor: 'contribute',
    title: 'The map grows with your help',
    body: "Have something to add? Suggest it! Bjj is an evolving art, everyone can help chart the grappling universe.",
    cta: 'Start rolling →',
  },
];

// Resolve `{desktop/mobile}` choice tokens in copy to the right variant.
function deviceText(s: string, isMobile: boolean): string {
  return s.replace(/\{([^/}]*)\/([^}]*)\}/g, (_, d, m) => (isMobile ? m : d));
}

const BUBBLE_W = 264;

type Arrow = 'up' | 'down' | 'left' | 'right' | 'none';
type Placement = {
  top: number;
  left: number;
  arrow: Arrow;
  /** Horizontal offset of the arrow for up/down placements. */
  arrowLeft: number;
  /** Vertical offset of the arrow for left/right placements. */
  arrowTop?: number;
};

// Measure an anchor as a viewport rect. 'node' and 'canvas' both resolve
// to the CANVAS CENTRE: the tour features a node by making it the camera's
// pivot, so it's pinned to the centre of the view. Anchoring to that fixed
// point (instead of the node's live per-frame projection) keeps the rect
// STABLE while the camera moves — the live projection jittered a pixel or
// two per frame from camera-update ordering, and each change fired a React
// re-render, which read as the card shaking during a drag. The rest are
// real DOM elements. Returns null while the target isn't available.
function anchorRect(anchor: Anchor): DOMRect | null {
  if (anchor === 'node' || anchor === 'canvas') {
    const el = document.querySelector('[data-tour="canvas"]');
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    return new DOMRect(cx - 1, cy - 1, 2, 2);
  }
  const el = document.querySelector(`[data-tour="${anchor}"]`);
  return el ? el.getBoundingClientRect() : null;
}

// Given an anchor rect, choose a clamped bubble position, arrow side, and
// the arrow's horizontal offset within the bubble (so it points at the
// anchor centre rather than sitting at a fixed corner).
//
// `prevArrow` feeds a HYSTERESIS band on the up/down decision: without it,
// a node parked near the vertical centre flips the bubble from below→above
// on every sub-pixel pan, which reads as the card vibrating. Once a side
// is chosen it sticks until the anchor moves well past centre.
function place(
  rect: DOMRect,
  prevArrow: Arrow,
  gap: number,
  bubbleH: number,
  /** When true, prefer sitting BESIDE the anchor (arrow points right at
   *  it) rather than above/below. Used for the Search tab: in landscape
   *  the panel is docked right, so the only room is to its left. */
  preferSide = false,
): Placement {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (preferSide) {
    // Enter from the left of the anchor, vertically centred on it and
    // clamped into the viewport.
    const top = Math.round(
      Math.max(12, Math.min(rect.top + rect.height / 2 - bubbleH / 2, vh - bubbleH - 12)),
    );
    const left = Math.round(Math.max(12, rect.left - BUBBLE_W - gap));
    // Arrow sits on the bubble's RIGHT edge, level with the anchor.
    const arrowTop = Math.max(
      14,
      Math.min(rect.top + rect.height / 2 - top, bubbleH - 14),
    );
    return { top, left, arrow: 'right', arrowLeft: 0, arrowTop };
  }
  const anchorCx = rect.left + rect.width / 2;
  const left = Math.max(12, Math.min(anchorCx - BUBBLE_W / 2, vw - BUBBLE_W - 12));
  const arrowLeft = Math.max(16, Math.min(anchorCx - left, BUBBLE_W - 16));
  // `below` = bubble sits below the anchor (arrow points UP at it). Keep
  // the current side across a dead zone (0.40vh–0.60vh) so tiny vertical
  // motion near centre doesn't flip it.
  let below: boolean;
  if (prevArrow === 'up') below = rect.top < vh * 0.6;
  else if (prevArrow === 'down') below = rect.top < vh * 0.4;
  else below = rect.top < vh * 0.5;
  if (below) {
    return { top: Math.round(rect.bottom + gap), left: Math.round(left), arrow: 'up', arrowLeft };
  }
  // Above the anchor: subtract the bubble's MEASURED height so a tall
  // card (e.g. the 5-line controls step) doesn't overrun the anchor and
  // cover it. `bubbleH` is measured from the live element each frame.
  return {
    top: Math.round(Math.max(12, rect.top - bubbleH - gap)),
    left: Math.round(left),
    arrow: 'down',
    arrowLeft,
  };
}

export function OnboardingCoach() {
  const tourRunning = useAppStore((s) => s.tourRunning);
  const setTourRunning = useAppStore((s) => s.setTourRunning);
  const setCoachNodeActive = useAppStore((s) => s.setCoachNodeActive);
  const setAutorotatePaused = useAppStore((s) => s.setAutorotatePaused);
  const setCoachHighlight = useAppStore((s) => s.setCoachHighlight);
  const setDrawerOpen = useAppStore((s) => s.setDrawerOpen);
  const setListViewOpen = useAppStore((s) => s.setListViewOpen);
  const reduced = usePrefersReducedMotion();
  const { isMobile } = useViewport();

  // Active flow: 'tour' with a step index, or null.
  const [tourStep, setTourStep] = useState<number | null>(null);
  const [pos, setPos] = useState<Placement | null>(null);
  const lastPos = useRef<string>('');
  const arrowRef = useRef<Arrow>('none');
  const bubbleRef = useRef<HTMLDivElement>(null);

  // The single active step.
  const active: Step | null = useMemo(
    () => (tourStep !== null ? TOUR[tourStep] : null),
    [tourStep],
  );

  // First map view (no front door anymore) → auto-run the tour once,
  // gated purely on the seen-tour flag. Replaying it from Settings clears
  // the flag and flips tourRunning directly.
  useEffect(() => {
    if (!flag.isSet(SEEN_TOUR)) {
      setTourRunning(true);
    }
  }, [setTourRunning]);

  // Store flag drives the tour.
  useEffect(() => {
    if (tourRunning && tourStep === null) {
      setTourStep(0);
    }
  }, [tourRunning, tourStep]);

  // While the TOUR is up, hold the map still so the featured node doesn't
  // drift out from under the bubble. Deliberately NOT restored on exit:
  // auto-rotate is off by default, so un-pausing here would silently turn
  // rotation ON for anyone who replays the tour. Leave it paused; the user
  // owns the setting from Settings → rotation.
  useEffect(() => {
    if (tourStep === null) return;
    setAutorotatePaused(true);
  }, [tourStep, setAutorotatePaused]);

  // Tell the scene to feature a hub node while the active step is a node
  // step (it selects + tracks it and publishes its screen position).
  useEffect(() => {
    setCoachNodeActive(active?.anchor === 'node');
    return () => setCoachNodeActive(false);
  }, [active, setCoachNodeActive]);

  // Per-step live surfaces — the tour opens/flashes the real UI it's
  // pointing at, then closes it on the next step (or when the tour ends):
  //   1 (view details) → pull out the detail drawer
  //   2 (search)       → open the Search panel, flash the filter input
  //   3 (controls)     → force-show + flash the bottom-right buttons
  //   4 (settings)     → flash the settings gear
  //   5 (grow)         → flash the contribute button
  // The KEY panel is deliberately left alone — no step covers it, so we
  // never open or close it out from under the user.
  // Runs after CoachNodeAnchor's selection effect (tree order), so the
  // drawer state here is authoritative for the featured node.
  useEffect(() => {
    if (tourStep === null) return;
    setDrawerOpen(tourStep === 1);
    setListViewOpen(tourStep === 2);
    setCoachHighlight(
      tourStep === 2
        ? 'search'
        : tourStep === 3
          ? 'controls'
          : tourStep === 4
            ? 'settings'
            : tourStep === 5
              ? 'contribute'
              : null,
    );
    return () => {
      setDrawerOpen(false);
      setListViewOpen(false);
      setCoachHighlight(null);
    };
  }, [tourStep, setCoachHighlight, setDrawerOpen, setListViewOpen]);

  // Reposition the bubble against its anchor every frame while a bubble is
  // up — the featured node's projected position moves as the camera eases
  // onto it, so we track it live (rAF, with a change-guard so we only
  // re-render when the placement actually shifts).
  useEffect(() => {
    if (!active) {
      setPos(null);
      lastPos.current = '';
      arrowRef.current = 'none';
      return;
    }
    // Fresh decision each step — don't inherit the previous step's side.
    arrowRef.current = 'none';
    let raf = 0;
    const sync = () => {
      const r = anchorRect(active.anchor);
      let nextPos: Placement;
      if (r) {
        // Node anchors need a big gap to clear the sphere's radius; DOM
        // anchors (KEY, contribute) sit tight to the element.
        const gap = active.anchor === 'node' ? 30 : 12;
        // Search tab: in landscape the panel is docked right, so enter
        // from its LEFT. In portrait the sheet is at the bottom, so fall
        // back to the normal above/below placement (enters from the top).
        const preferSide = active.anchor === 'search-tab' && !isMobile;
        // Measure the live bubble so an "above" placement clears the
        // anchor by the card's true height (falls back before first paint).
        const bubbleH = bubbleRef.current?.offsetHeight ?? 160;
        nextPos = place(r, arrowRef.current, gap, bubbleH, preferSide);
        arrowRef.current = nextPos.arrow;
      } else {
        // Anchor not available yet (KEY hidden, or the node projection
        // hasn't published). Fall back to a sensible fixed spot so the
        // bubble still shows and the user can advance / skip.
        const vw = window.innerWidth;
        nextPos = {
          top: 64,
          left: Math.max(12, vw - BUBBLE_W - 16),
          arrow: 'none',
          arrowLeft: 20,
        };
      }
      const key = `${nextPos.top}|${nextPos.left}|${nextPos.arrow}|${nextPos.arrowLeft}|${nextPos.arrowTop ?? ''}`;
      if (key !== lastPos.current) {
        lastPos.current = key;
        setPos(nextPos);
      }
      raf = requestAnimationFrame(sync);
    };
    sync();
    return () => cancelAnimationFrame(raf);
  }, [active, isMobile]);

  if (!active || !pos) return null;

  const isTour = tourStep !== null;
  const isLast = isTour && tourStep === TOUR.length - 1;

  const finishTour = () => {
    flag.set(SEEN_TOUR);
    setTourRunning(false);
    setTourStep(null);
  };
  const advance = () => {
    if (isLast) return finishTour();
    setTourStep((s) => (s === null ? null : s + 1));
  };
  const back = () => setTourStep((s) => (s === null ? null : Math.max(0, s - 1)));
  const skip = finishTour;
  // First step offers "skip"; from step 2 on the left control is "← back".
  const canGoBack = (tourStep ?? 0) > 0;

  return createPortal(
    <div
      ref={bubbleRef}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: BUBBLE_W,
        zIndex: 2500,
        background: GLASS.raisedSoft,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: `1px solid ${LINE2}`,
        borderRadius: RADIUS.lg,
        boxShadow: '0 14px 40px rgba(0,0,0,0.55)',
        padding: 15,
        color: INK,
        fontFamily: FONT.ui,
        animation: reduced ? undefined : 'bjjmap-coach-in 260ms ease both',
      }}
      role="dialog"
      aria-label={active.title}
    >
      {/* Little pointer toward the anchor. A rotated square whose two
          light borders form the visible tip; which corner points out
          depends on the side the bubble was placed on. */}
      {pos.arrow !== 'none' && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            ...(pos.arrow === 'up' || pos.arrow === 'down'
              ? {
                  left: pos.arrowLeft,
                  marginLeft: -5,
                  [pos.arrow === 'up' ? 'top' : 'bottom']: -6,
                }
              : {
                  top: pos.arrowTop ?? 20,
                  marginTop: -5,
                  [pos.arrow === 'left' ? 'left' : 'right']: -6,
                }),
            width: 10,
            height: 10,
            background: GLASS.raisedSoft,
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderLeft: `1px solid ${LINE2}`,
            borderTop: `1px solid ${LINE2}`,
            transform:
              pos.arrow === 'up'
                ? 'rotate(45deg)'
                : pos.arrow === 'down'
                  ? 'rotate(225deg)'
                  : pos.arrow === 'left'
                    ? 'rotate(-45deg)'
                    : 'rotate(135deg)',
          }}
        />
      )}
      {isTour && (
        <div
          style={{
            fontFamily: FONT.mono,
            fontSize: 10,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: INK3,
            marginBottom: 8,
          }}
        >
          Step {(tourStep ?? 0) + 1} / {TOUR.length}
        </div>
      )}
      <div
        style={{
          fontFamily: FONT.display,
          fontSize: 15,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: GOLD,
          marginBottom: 6,
        }}
      >
        {deviceText(active.title, isMobile)}
      </div>
      <div style={{ fontSize: 13, color: INK2, lineHeight: 1.5 }}>
        {deviceText(active.body, isMobile)}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 12,
        }}
      >
        <button
          onClick={canGoBack ? back : skip}
          style={{
            fontFamily: FONT.ui,
            background: 'none',
            border: 'none',
            color: INK3,
            fontSize: 12,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {canGoBack ? '← back' : 'skip'}
        </button>
        <button
          onClick={advance}
          style={{
            fontFamily: FONT.ui,
            background: GOLD,
            color: GOLD_INK,
            border: 0,
            borderRadius: RADIUS.sm,
            padding: '6px 15px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: `0 0 16px ${hexA(GOLD, 0.25)}`,
          }}
        >
          {active.cta}
        </button>
      </div>
      <style>{`
        @keyframes bjjmap-coach-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>,
    document.body,
  );
}
